import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Нижняя шторка с тремя якорями 40/75/95 процентов (05 §4).
 *
 * Якорь выбирается по СКОРОСТИ жеста, а не только по конечной точке: медленное
 * перетаскивание уточняет высоту, быстрый бросок перекидывает через якорь.
 * Ручка сделана крупной цели касания - инвариант 10 требует 44 пикселя.
 */
const ANCHORS = [0.4, 0.75, 0.95] as const;

export function Sheet({ children, label }: { children: ReactNode; label: string }) {
  const [anchor, setAnchor] = useState(0);
  const drag = useRef<{ startY: number; startAt: number; startAnchor: number } | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  useEffect(() => {
    const onUp = (): void => {
      drag.current = null;
      setDragOffset(0);
    };
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const height = `calc(${(ANCHORS[anchor] ?? 0.4) * 100}dvh - ${dragOffset}px)`;

  return (
    <div
      className="fixed inset-x-0 bottom-[var(--spacing-tabbar)] z-30 flex flex-col rounded-t-[10px] border-t border-line2 bg-panel"
      style={{ height }}
      aria-label={label}
    >
      <div
        role="separator"
        aria-label={label}
        className="flex h-[var(--spacing-touch)] cursor-grab items-center justify-center touch-none"
        onPointerDown={(e) => {
          drag.current = { startY: e.clientY, startAt: performance.now(), startAnchor: anchor };
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          setDragOffset(e.clientY - drag.current.startY);
        }}
        onPointerUp={(e) => {
          const d = drag.current;
          drag.current = null;
          setDragOffset(0);
          if (!d) return;
          const dy = e.clientY - d.startY;
          const speed = Math.abs(dy) / Math.max(1, performance.now() - d.startAt);
          // Быстрый бросок - шаг через якорь, медленный - на соседний.
          const step = speed > 0.6 ? 2 : 1;
          const dir = dy < 0 ? 1 : -1;
          const next = Math.min(ANCHORS.length - 1, Math.max(0, d.startAnchor + dir * step));
          setAnchor(next);
        }}
      >
        <span className="h-1 w-11 rounded-full bg-line2" />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">{children}</div>
    </div>
  );
}
